<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Aggregation" tab.

    WHAT: Owns how the metric rolls up — the aggregation_type, primary_key_columns
      (the grain the engine COUNT(DISTINCT)s), and value_conditions (the numerator
      match, default VALUE IS NOT NULL). Wraps the reused MetricComputePanel.

    CONTRACT (props { record, artifact, context }):
      READS  record.composable_config.studio_config.{aggregation_type,
             primary_key_columns, value_conditions}; also watches
             record.composable_config to (re)resolve output columns.
      EMITS  patch({ composable_config: { ...config, studio_config } }) — merges
             ONLY aggregation_type / primary_key_columns / value_conditions back
             into the existing studio_config (leaves steps/join graph untouched,
             the mirror of DataSourceSection).

    NOTES:
      - Resolves real output columns for the PK/numerator pickers by GETting
        /api/metrics/workflow-columns/{workflow_id}/ per step (loadColumns).
      - hydrating guard: set false on $nextTick after mount; emit() returns early
        while hydrating, so mounting the deep `compute` watcher fires no spurious
        patch.
      - NOT selfChrome — rendered inside ArtifactDetail's standard titled card.
  -->
  <div>
    <LoadError v-if="columnsLoadError" retryable class="mb-2" message="Couldn't load this workflow's steps, so no columns are listed." @retry="loadColumns" />
    <!-- Calculation grain: which precompute track this metric runs on.
         'npi' (claims) = patient/NPI facts crosswalked to entities (default).
         'entity' = facts born at entity grain (engagements, tiers, implementation
         status) — DA emits ENTITY_ID/YEAR/OBSERVATION_DATE, output goes to
         COMPOSABLE_METRIC_ENTITY_LEVEL, rollups via the entity hierarchy. -->
    <div class="mb-4 pb-4 border-b border-gray-200">
      <label class="block text-xs font-semibold text-gray-600 mb-1">Calculation grain</label>
      <select
        :value="grain"
        @change="onGrainChange($event.target.value)"
        class="text-xs px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none"
      >
        <option value="npi">Claims (patient / NPI — crosswalked to entities)</option>
        <option value="entity">Entity (facts born at entity level — engagements, tiers, status)</option>
      </select>
      <p v-if="grain === 'entity'" class="mt-1.5 text-[11px] text-gray-500 leading-snug">
        Data Source must emit one row per entity per observation date:
        <span class="font-mono">ENTITY_ID</span>, <span class="font-mono">YEAR</span>,
        <span class="font-mono">OBSERVATION_DATE</span> (the engine injects TENANT_ID).
        The DA generates its own weekly date series (latest = today) and computes the
        fact as of each date — every precompute reproduces the full history. No date
        spine step, population, or EFCTV; sum_ratio is not supported. Rollups
        (PL / Market / Tenant) come from the entity hierarchy at read time.
      </p>
    </div>
    <MetricComputePanel
      v-model="compute"
      :output-columns="outputColumns"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { consumerSteps } from '@/utils/consumerSteps';
import { notifyLoadError } from '@/utils/loadError.js';
import LoadError from '@/components/shared/LoadError.vue';
import MetricComputePanel from '@/components/Admin/MetricStudio/panels/MetricComputePanel.vue';

export default {
  name: 'AggregationSection',
  components: { LoadError, MetricComputePanel },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    const sc = (this.record.composable_config || {}).studio_config || {};
    return {
      outputColumns: [],
      columnsLoadError: false,
      grain: sc.grain === 'entity' ? 'entity' : 'npi',
      compute: {
        aggregation_type: sc.aggregation_type || 'percentage',
        primary_key_columns: [...(sc.primary_key_columns || ['BENE_MBI_ID'])],
        value_conditions: (sc.value_conditions && sc.value_conditions.length)
          ? sc.value_conditions.map((c) => ({ ...c }))
          : [{ column: 'VALUE', op: 'IS NOT NULL', value: '' }],
      },
      hydrating: true,
    };
  },
  computed: {
    studioConfig() { return (this.record.composable_config || {}).studio_config || {}; },
  },
  watch: {
    compute: { deep: true, handler() { this.emit(); } },
    'record.composable_config': { deep: true, handler() { this.loadColumns(); } },
  },
  async mounted() {
    await this.loadColumns();
    this.$nextTick(() => { this.hydrating = false; });
  },
  methods: {
    onGrainChange(value) {
      this.grain = value === 'entity' ? 'entity' : 'npi';
      // Swap the pk DEFAULT with the grain (only when the user hasn't customized
      // it): entity metrics count ENTITY_ID, claims metrics count BENE_MBI_ID.
      const pk = this.compute.primary_key_columns || [];
      const isDefault = (arr, v) => arr.length === 1 && arr[0] === v;
      if (this.grain === 'entity' && isDefault(pk, 'BENE_MBI_ID')) {
        this.compute.primary_key_columns = ['ENTITY_ID'];
      } else if (this.grain === 'npi' && isDefault(pk, 'ENTITY_ID')) {
        this.compute.primary_key_columns = ['BENE_MBI_ID'];
      }
      // pk mutation above triggers the deep compute watcher → emit(); when the
      // pk was customized nothing else fires, so emit directly (debounced PATCH
      // upstream dedupes).
      this.emit();
    },
    async loadColumns() {
      // Resolve output columns from the join graph's DAs so the PK + numerator
      // pickers offer real columns.
      let steps = [];
      try {
        steps = await consumerSteps(this.studioConfig);
      } catch (error) {
        notifyLoadError(error, { feature: 'metric-studio-columns', endpoint: '/api/etl_module/data-workflows/' });
        this.columnsLoadError = true;
        return;
      }
      this.columnsLoadError = false;
      const cols = [];
      const seen = new Set();
      const attributes = (this.context && (this.context.sources || this.context.attributes)) || [];
      for (const s of steps) {
        if (!s.workflow_id) continue;
        const label = (attributes.find((a) => a.workflow_id === s.workflow_id) || {}).id || s.workflow_id;
        try {
          const { data } = await axios.get(`/api/metrics/workflow-columns/${s.workflow_id}/`);
          for (const c of data.columns || []) {
            if (!seen.has(c)) { seen.add(c); cols.push({ col: c, workflow_id: s.workflow_id, label }); }
          }
        } catch { /* ignore */ }
      }
      this.outputColumns = cols;
    },
    emit() {
      if (this.hydrating) return;
      const sc = { ...this.studioConfig };
      sc.aggregation_type = this.compute.aggregation_type;
      sc.primary_key_columns = [...(this.compute.primary_key_columns || [])];
      sc.value_conditions = (this.compute.value_conditions || [])
        .filter((c) => c.column)
        .map((c) => ({ column: c.column, op: c.op, value: c.value }));
      // grain: 'entity' is explicit; the npi default stays UNWRITTEN so existing
      // configs keep their exact shape (absent = claims track).
      if (this.grain === 'entity') sc.grain = 'entity';
      else delete sc.grain;
      this.$emit('patch', { composable_config: { ...(this.record.composable_config || {}), studio_config: sc } });
    },
  },
};
</script>
